import { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { PageHeader, PageBody, StatCell, Section } from "../components/layout/Page";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import { Users, ClipboardCheck, AirplaneTilt, UserCirclePlus, TrendUp, Warning as WarningIcon } from "@phosphor-icons/react";

const COLORS = ["#2563EB", "#12B886", "#FAB005", "#F54236", "#64748B", "#7C3AED"];

export default function Dashboard() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [longBreaks, setLongBreaks] = useState([]);

  const scope = user?.role === "super_admin" ? null : activeCompanyId;
  const canSeeBreaches = ["super_admin", "hr_admin", "manager"].includes(user?.role);

  useEffect(() => {
    const load = async () => {
      const params = scope ? { company_id: scope } : {};
      const [d, t, a] = await Promise.all([
        api.get("/reports/dashboard", { params }),
        api.get("/reports/attendance-trend", { params: { ...params, days: 7 } }),
        api.get("/announcements", { params }),
      ]);
      setData(d.data);
      setTrend(t.data);
      setAnnouncements(a.data.slice(0, 4));
      if (canSeeBreaches) {
        try {
          const lb = await api.get("/reports/long-breaks", { params: { ...params, threshold_minutes: 40 } });
          setLongBreaks(lb.data);
        } catch {
          setLongBreaks([]);
        }
      }
    };
    load();
  }, [scope, canSeeBreaches]);

  if (!data) {
    return (
      <PageBody>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border rounded-sm">
          {[0,1,2,3].map(i => <div key={i} className="bg-white h-28 animate-pulse" />)}
        </div>
      </PageBody>
    );
  }

  const recruitmentData = Object.entries(data.recruitment_by_stage || {}).map(([k, v]) => ({ name: k, value: v }));
  const leavesData = Object.entries(data.leaves_by_status || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow={user?.role === "super_admin" ? "Group overview" : "Business overview"}
        title="Dashboard"
        subtitle="Pulse of attendance, leave, payroll and hiring across your business."
      />
      <PageBody className="space-y-6">
        {canSeeBreaches && longBreaks.length > 0 && (
          <div className="border-l-4 border-[#F54236] bg-[#FEE2E2] rounded-sm p-4" data-testid="long-break-alert">
            <div className="flex items-center gap-2 mb-2">
              <WarningIcon size={16} className="text-[#F54236]" weight="fill" />
              <div className="text-sm font-semibold uppercase tracking-wider text-[#991B1B]">
                Long break alert — {longBreaks.length} employee{longBreaks.length !== 1 ? "s" : ""} today
              </div>
            </div>
            <div className="text-xs text-[#7F1D1D] mb-3">
              Employees who used 40+ minutes of break time today (45-min daily cap).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {longBreaks.slice(0, 9).map((b) => (
                <div key={b.employee_id} className="bg-white border border-[#FCA5A5] rounded-sm p-2 text-xs" data-testid={`long-break-${b.employee_code}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#0F172A]">{b.full_name}</span>
                    <span className={`font-mono font-semibold ${b.total_break_minutes >= 45 ? "text-[#F54236]" : "text-[#FAB005]"}`}>
                      {b.total_break_minutes} / {b.max_minutes}m
                    </span>
                  </div>
                  <div className="text-[10px] text-[#64748B] font-mono mt-0.5">{b.employee_code} · {b.department}</div>
                </div>
              ))}
            </div>
            {longBreaks.length > 9 && (
              <div className="text-[11px] text-[#7F1D1D] mt-2">+ {longBreaks.length - 9} more on the Attendance page.</div>
            )}
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell testId="kpi-total-employees" label="Headcount" value={data.total_employees} hint="Active employees" />
          <StatCell testId="kpi-present-today" label="Present Today" value={data.present_today} hint="Across selected scope" accent="text-[#12B886]" />
          <StatCell testId="kpi-pending-leaves" label="Pending Leaves" value={data.pending_leaves} hint="Needs approval" accent="text-[#FAB005]" />
          <StatCell testId="kpi-open-positions" label="Open Positions" value={data.open_positions} hint="In recruitment pipeline" accent="text-[#2563EB]" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Section title="Attendance · last 7 days">
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }} />
                    <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          <Section title="Recruitment funnel">
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={recruitmentData} dataKey="value" nameKey="name" outerRadius={80} innerRadius={40}>
                    {recruitmentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {user?.role === "super_admin" && (
            <Section title="Headcount by business">
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={data.by_company} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke="#E2E8F0" />
                    <XAxis dataKey="company" tick={{ fontSize: 9, fill: "#475569" }} angle={-12} textAnchor="end" height={48} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#2563EB" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          <Section title="Leave by status">
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={leavesData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }} />
                  <Bar dataKey="value" fill="#12B886" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section title="Payroll · latest cycles">
            <div className="space-y-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">Total net disbursed</div>
                  <div className="font-display text-3xl tracking-tight font-medium mt-2">{formatINR(data.payroll?.total_net || 0)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">Payslips</div>
                  <div className="font-display text-3xl tracking-tight font-medium mt-2">{data.payroll?.count || 0}</div>
                </div>
              </div>
              <div className="text-xs text-[#475569] flex items-center gap-2 pt-3 border-t border-border">
                <TrendUp size={14} className="text-[#12B886]" />
                <span>Cross-business rollup of generated payslips.</span>
              </div>
            </div>
          </Section>
        </div>

        <Section title="Latest announcements">
          {announcements.length === 0 ? (
            <p className="text-sm text-[#64748B]">No announcements yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {announcements.map((a) => (
                <li key={a.id} className="py-3 flex items-start gap-3" data-testid={`announcement-${a.id}`}>
                  <span className={`mt-1.5 inline-block w-1.5 h-1.5 ${a.priority === "high" ? "bg-[#F54236]" : "bg-[#2563EB]"}`} />
                  <div>
                    <div className="text-sm font-medium text-[#0F172A]">{a.title}</div>
                    <div className="text-xs text-[#475569] mt-0.5">{a.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {data.compliance && (
          <Section
            title="Compliance breach summary"
            action={
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5 text-[#F54236]">
                <WarningIcon size={12} weight="bold" />
                {data.compliance.total_breaches} open
              </span>
            }
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border rounded-sm overflow-hidden" data-testid="compliance-grid">
              {[
                { k: "pending_leave_approvals", label: "Leave approvals" },
                { k: "pending_attendance_corrections", label: "Attendance fixes" },
                { k: "employees_missing_aadhaar", label: "Missing Aadhaar" },
                { k: "employees_missing_pan", label: "Missing PAN" },
                { k: "late_marks_this_month", label: "Late marks (mo.)" },
              ].map((m) => (
                <div key={m.k} className="p-4 bg-white" data-testid={`compliance-${m.k}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">{m.label}</div>
                  <div className={`font-display text-2xl mt-2 ${data.compliance[m.k] > 0 ? "text-[#F54236]" : "text-[#0F172A]"}`}>
                    {data.compliance[m.k]}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}
      </PageBody>
    </div>
  );
}
